/**
 * Dashboard — Wave 2 (Signature Authenticated Routes) executive rewrite.
 *
 * Rebuilt against the LandingV2 aesthetic bar:
 *   • White surface (`--ds-color-surface`) as primary canvas.
 *   • Warm cream page background (`--ds-color-background`) as separator.
 *   • Cards use `shadow-[var(--ds-shadow-card)]` + a 1px hairline border,
 *     `--ds-radius-lg`/`xl` max — NO 24px+ blob radii.
 *   • Uppercase eyebrows: `text-[11px] tracking-[0.14em] font-semibold`.
 *   • Display headings use `--ds-font-display` at semibold with
 *     `letter-spacing: -0.02em`.
 *   • Every `v2-*` variable / inline hex / pastel warm banner / consumer
 *     greeting removed — tier signal now surfaces as a `SemanticStatusBadge`
 *     or `Badge` chip beside a neutral H1 rather than a colored H1.
 *
 * Material-scope preserved:
 *   • Every `data-testid` on the previous Dashboard is preserved verbatim
 *     so behavioural tests continue to resolve.
 *   • `GET /api/dashboard/summary` + `GET /api/site-settings` fetches,
 *     achievement one-shot toast + POST celebration ack, and all inline
 *     component composition (SecondaryNavCard, ConnectCreditPanel,
 *     ReadinessGapCard, EmptyState / MilestoneCard / StatCallout).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronRight,
  Compass,
  FileText,
  Handshake,
  Home,
  Target,
  TrendingUp,
  Trophy,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import StatCallout from "@/design-system/components/StatCallout";
import MilestoneCard from "@/design-system/components/MilestoneCard";
import EmptyState from "@/components/v2/EmptyState";
import { Badge, SemanticStatusBadge } from "@/design-system";
import ConnectCreditPanel from "@/components/dashboard/ConnectCreditPanel";
import ReadinessGapCard from "@/components/marketplace/ReadinessGapCard";

const CATEGORY_TONE = {
  funding_ready:     "success",
  nearly_ready:      "warning",
  needs_improvement: "warning",
  build_foundation:  "info",
};

const MORTGAGE_TONE = {
  mortgage_ready:    "success",
  nearly_ready:      "warning",
  needs_improvement: "warning",
  build_foundation:  "info",
};

const NBA_ICONS = {
  complete_profile:      Target,
  upload_report:         Upload,
  review_analysis:       TrendingUp,
  generate_first_letter: FileText,
  improve_credit:        Compass,
  view_partners:         Handshake,
  review_mortgage:       Home,
};

/* --------------------------------- root --------------------------------- */

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/dashboard/summary")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "We couldn't load your dashboard just now.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    api
      .get("/site-settings")
      .then((r) => { if (!cancelled) setSiteSettings(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      data-testid="dashboard-root"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="mx-auto max-w-6xl px-4 md:px-8 py-10 md:py-14 space-y-10">
        {loading ? (
          <SkeletonHero />
        ) : error ? (
          <ErrorPanel message={error} />
        ) : summary ? (
          <>
            <HeroCommandCenter summary={summary} user={user} />
            <BelowTheFold summary={summary} user={user} siteSettings={siteSettings} />
          </>
        ) : null}
      </main>
    </div>
  );
}

/* -------------------------- signature hero ----------------------------- */

function HeroCommandCenter({ summary, user }) {
  const {
    readiness_score, readiness_category, credit_scores, mortgage_score, mortgage_tier,
    next_best_action, journey, opportunity, best_match, achievements, motivation_line,
    quick_stats, compliance_short,
  } = summary;

  return (
    <section data-testid="dashboard-hero" className="space-y-10">
      {/* Formal page header — no personal greeting, no flavor copy. */}
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
          Command Center
        </div>
        <h1
          data-testid="dashboard-greeting"
          className="mt-2 text-[36px] md:text-[44px] leading-tight font-semibold text-[var(--ds-color-ink)]"
          style={{ fontFamily: "var(--ds-font-display)", letterSpacing: "-0.02em" }}
        >
          Financial Readiness
        </h1>
        <p
          data-testid="dashboard-page-subtitle"
          className="mt-3 text-[15px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl"
        >
          Your readiness signal, next best action, and progress toward funding — one workspace.
        </p>
      </header>

      <ReadinessSnapshot
        score={readiness_score}
        category={readiness_category}
        creditScores={credit_scores}
        mortgageScore={mortgage_score}
        mortgageTier={mortgage_tier}
      />

      <NextBestAction action={next_best_action} />

      <JourneyStrip journey={journey} />

      {opportunity ? <OpportunityCard opportunity={opportunity} /> : null}

      <BestMatchOrUnlock bestMatch={best_match} />

      <AchievementsStrip achievements={achievements} />

      <OpportunitiesHeroCard summary={summary} />

      <MotivationAndStats
        motivationLine={motivation_line}
        quickStats={quick_stats}
        complianceShort={compliance_short}
      />
    </section>
  );
}

/* ---------------------- 1 · Readiness snapshot panel ------------------- */

function ReadinessSnapshot({ score, category, creditScores, mortgageScore, mortgageTier }) {
  const categoryTone = category ? (CATEGORY_TONE[category.code] || "info") : null;
  const mortgageValid = mortgageScore != null && mortgageTier && mortgageTier.code && mortgageTier.code !== "intake_required";
  const mortgageT = mortgageValid ? (MORTGAGE_TONE[mortgageTier.code] || "info") : null;

  const displayScore = score != null ? score : "—";
  const pct = typeof score === "number" ? Math.max(0, Math.min(100, score)) : 0;

  return (
    <section
      data-testid="dashboard-snapshot"
      className="rounded-[var(--ds-radius-xl)] overflow-hidden"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      {/* Score panel */}
      <div className="p-6 md:p-8 grid gap-6 md:grid-cols-[1.15fr_1fr] items-start">
        {/* Left — score + tier + progress */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
            Funding Readiness Score
          </div>
          <div className="mt-2 flex items-baseline gap-4 flex-wrap">
            <div
              data-testid="dashboard-tile-funding-readiness"
              className="tabular-nums text-[var(--ds-color-ink)]"
              style={{
                fontSize: "3.5rem",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
              }}
            >
              {displayScore}
              <span
                className="text-base font-medium ml-1 text-[var(--ds-color-ink-muted)]"
                style={{ fontFamily: "var(--ds-font-sans)" }}
              >
                / 100
              </span>
            </div>
            {category ? (
              <Badge
                tone={categoryTone}
                data-testid="dashboard-category-pill"
                size="md"
              >
                <SemanticStatusBadge
                  emoji={category.emoji}
                  hideLabel
                  size="sm"
                  data-testid="dashboard-category-pill-icon"
                  className="border-transparent bg-transparent px-0 h-4"
                />
                <span>{category.label}</span>
                <span className="opacity-50">·</span>
                <span className="tabular-nums">{category.score}/100</span>
              </Badge>
            ) : null}
          </div>

          <div
            className="mt-6 h-2 rounded-[var(--ds-radius-pill)] overflow-hidden"
            style={{ background: "var(--ds-color-surface-inset)" }}
          >
            <div
              className="h-full rounded-[var(--ds-radius-pill)] transition-all"
              style={{
                width: `${pct}%`,
                background: "var(--ds-color-accent)",
                transitionDuration: "var(--ds-duration-slow)",
              }}
            />
          </div>

          {mortgageValid ? (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                Mortgage readiness
              </span>
              <Badge
                tone={mortgageT}
                data-testid="dashboard-mortgage-pill"
                size="md"
              >
                <SemanticStatusBadge
                  emoji={mortgageTier.emoji}
                  hideLabel
                  size="sm"
                  data-testid="dashboard-mortgage-pill-icon"
                  className="border-transparent bg-transparent px-0 h-4"
                />
                <span>{mortgageTier.label}</span>
                <span className="opacity-50">·</span>
                <span className="tabular-nums">{mortgageScore}/100</span>
              </Badge>
            </div>
          ) : null}
        </div>

        {/* Right — credit bureau tiles */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
            Bureau scores
          </div>
          <div className="mt-3 grid gap-3 grid-cols-1 sm:grid-cols-3">
            {["TransUnion", "Equifax", "Experian"].map((bureau) => {
              const value = creditScores?.[bureau];
              return (
                <BureauTile
                  key={bureau}
                  testId={`dashboard-tile-credit-${bureau.toLowerCase()}`}
                  label={bureau}
                  value={value ?? "—"}
                />
              );
            })}
          </div>
          {mortgageValid ? (
            <div className="mt-3">
              <BureauTile
                testId="dashboard-tile-mortgage"
                label="Mortgage"
                value={mortgageScore}
                accent
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function BureauTile({ label, value, testId, accent = false }) {
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-md)] px-4 py-3"
      style={{
        background: "var(--ds-color-surface-muted)",
        border: "1px solid var(--ds-color-border)",
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label}
      </div>
      <div
        className="mt-1 tabular-nums text-[var(--ds-color-ink)]"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          fontSize: "1.5rem",
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          color: accent ? "var(--ds-color-accent)" : "var(--ds-color-ink)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ---------------------- 2 · Next Best Action -------------------------- */

function NextBestAction({ action }) {
  if (!action) return null;
  const Icon = NBA_ICONS[action.code] || Target;
  return (
    <section
      data-testid="dashboard-next-best-action"
      className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-start gap-4 min-w-0">
        <span
          className="inline-flex h-10 w-10 items-center justify-center flex-shrink-0 rounded-[var(--ds-radius-md)]"
          style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]">
            Next best step
          </div>
          <div
            data-testid="dashboard-nba-label"
            className="mt-1 text-[20px] leading-tight text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600 }}
          >
            {action.label}
          </div>
        </div>
      </div>
      <Link
        data-testid="dashboard-nba-cta"
        to={action.route}
        className="inline-flex items-center justify-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2.5 text-[14px] font-semibold flex-shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
        style={{
          background: "var(--ds-color-accent)",
          color: "var(--ds-color-accent-ink)",
          transitionDuration: "var(--ds-duration-fast)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-accent-strong)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-color-accent)")}
      >
        Take action
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </section>
  );
}

/* ---------------------- 3 · Journey strip ----------------------------- */

function JourneyStrip({ journey }) {
  if (!journey?.steps?.length) return null;
  return (
    <section data-testid="dashboard-journey" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
          Progress journey
        </div>
        <div className="text-[13px] text-[var(--ds-color-ink-muted)]">
          Step <span className="tabular-nums font-semibold text-[var(--ds-color-ink)]">{journey.current_index}</span> of 5
          {journey.override_active ? (
            <span
              data-testid="dashboard-journey-override-badge"
              className="ml-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]"
              title="Admin override active"
            >
              (pinned)
            </span>
          ) : null}
        </div>
      </div>
      <ol className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {journey.steps.map((s) => (
          <li
            key={s.index}
            data-testid={`dashboard-journey-step-${s.index}`}
            data-current={s.current ? "true" : "false"}
            data-completed={s.completed ? "true" : "false"}
            className="rounded-[var(--ds-radius-md)] border p-3 text-[13px] flex items-start gap-2"
            style={{
              background: s.completed
                ? "var(--ds-color-success-soft)"
                : s.current
                ? "var(--ds-color-accent-soft)"
                : "var(--ds-color-surface)",
              borderColor: s.completed
                ? "var(--ds-color-success)"
                : s.current
                ? "var(--ds-color-accent)"
                : "var(--ds-color-border)",
              color: "var(--ds-color-ink)",
              opacity: !s.current && !s.completed ? 0.85 : 1,
            }}
          >
            <span aria-hidden="true" className="mt-0.5 flex-shrink-0">
              {s.completed ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: "var(--ds-color-success)" }} />
              ) : s.current ? (
                <span
                  className="inline-block h-3.5 w-3.5 rounded-[var(--ds-radius-sm)]"
                  style={{ background: "var(--ds-color-accent)" }}
                />
              ) : (
                <span
                  className="inline-block h-3.5 w-3.5 rounded-[var(--ds-radius-sm)] border"
                  style={{ borderColor: "var(--ds-color-border-strong)" }}
                />
              )}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                Step {s.index}
              </div>
              <div className="text-[13px] leading-snug text-[var(--ds-color-ink)]">{s.label}</div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ---------------------- 4 · Opportunity ------------------------------- */

function OpportunityCard({ opportunity }) {
  const label = {
    business_funding:    "Estimated business funding",
    home_purchase_power: "Estimated home purchase power",
    partner_count:       "Funding partners you match",
  }[opportunity.type] || "Opportunity";
  return (
    <section
      data-testid="dashboard-opportunity"
      className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex items-start gap-4"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <span
        className="inline-flex h-10 w-10 items-center justify-center flex-shrink-0 rounded-[var(--ds-radius-md)]"
        style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
      >
        <Award className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
          {label}
        </div>
        <div
          data-testid="dashboard-opportunity-value"
          className="mt-1 tabular-nums text-[var(--ds-color-ink)]"
          style={{
            fontFamily: "var(--ds-font-display)",
            fontWeight: 600,
            fontSize: "1.5rem",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {opportunity.value_display}
        </div>
        <div className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)]">
          What you&rsquo;re working toward — sharpens as your readiness grows.
        </div>
      </div>
    </section>
  );
}

/* ---------------------- 5 · Best match / unlock plan ------------------ */

function BestMatchOrUnlock({ bestMatch }) {
  if (!bestMatch) return null;
  if (bestMatch.kind === "match") {
    return (
      <section
        data-testid="dashboard-best-match"
        className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-border)",
          boxShadow: "var(--ds-shadow-card)",
        }}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]">
              You match {bestMatch.total_matches} funding partner{bestMatch.total_matches === 1 ? "" : "s"}
            </div>
            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
              <div
                className="text-[var(--ds-color-ink)]"
                style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.2 }}
              >
                Best match: <span data-testid="dashboard-best-match-name">{bestMatch.partner_name}</span>
              </div>
              <Badge tone="success" data-testid="dashboard-best-match-percent">
                {bestMatch.match_percent}% match
              </Badge>
            </div>
            <div className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)]">
              Estimated funding: <span className="tabular-nums text-[var(--ds-color-ink)]">{bestMatch.funding_display || "—"}</span>
            </div>
          </div>
          <Building2
            className="h-8 w-8 flex-shrink-0 hidden md:block"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
        </div>
        {bestMatch.reasons?.length ? (
          <div className="mt-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2">
              Why you match
            </div>
            <ul className="grid gap-2">
              {bestMatch.reasons.slice(0, 3).map((r, i) => (
                <li
                  key={i}
                  data-testid={`dashboard-best-match-reason-${i}`}
                  className="flex items-start gap-2 text-[13px] text-[var(--ds-color-ink)]"
                >
                  <CheckCircle2
                    className="h-4 w-4 flex-shrink-0 mt-0.5"
                    style={{ color: "var(--ds-color-success)" }}
                    aria-hidden="true"
                  />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="mt-5">
          <Link
            to="/partners"
            data-testid="dashboard-best-match-view-all"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
          >
            View all {bestMatch.total_matches} match{bestMatch.total_matches === 1 ? "" : "es"}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    );
  }
  return (
    <section
      data-testid="dashboard-unlock-plan"
      className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div
        className="text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.2 }}
      >
        Unlock your first funding matches.
      </div>
      <p className="mt-2 text-[14px] text-[var(--ds-color-ink-muted)] leading-relaxed">
        Complete these steps to unlock your first funding partner matches:
      </p>
      <ul className="mt-4 grid gap-2">
        {(bestMatch.recommendations || []).map((r, i) => (
          <li
            key={i}
            data-testid={`dashboard-unlock-recommendation-${i}`}
            className="flex items-start gap-2 text-[14px] text-[var(--ds-color-ink)]"
          >
            <span
              aria-hidden="true"
              className="mt-2 h-1.5 w-1.5 rounded-[var(--ds-radius-pill)] flex-shrink-0"
              style={{ background: "var(--ds-color-accent)" }}
            />
            <span>{r}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 text-[13px] text-[var(--ds-color-ink-muted)]">
        Estimated partners you&rsquo;ll unlock:{" "}
        <span
          data-testid="dashboard-unlock-estimated"
          className="tabular-nums font-semibold text-[var(--ds-color-ink)]"
        >
          {bestMatch.estimated_unlocks}
        </span>
      </div>
    </section>
  );
}

/* ---------------------- 6 · Achievements + one-shot toast ------------- */

function AchievementsStrip({ achievements }) {
  const items = useMemo(() => achievements || [], [achievements]);
  const toToast = useMemo(() => items.filter((a) => a.needs_toast), [items]);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !toToast.length) return;
    firedRef.current = true;
    toToast.forEach((a) => {
      toast.success(a.label, {
        description: "New milestone completed.",
        duration: 6000,
      });
      api.post(`/dashboard/milestones/${encodeURIComponent(a.key)}/celebrated`).catch(() => {});
    });
  }, [toToast]);

  if (!items.length) return null;
  return (
    <section data-testid="dashboard-achievements" className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        Achievements
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((a) => (
          <Badge
            key={a.key}
            tone="success"
            data-testid={`dashboard-achievement-${a.key}`}
            size="md"
          >
            <Trophy className="h-3.5 w-3.5" aria-hidden="true" />
            {a.label}
          </Badge>
        ))}
      </div>
    </section>
  );
}

/* ---------------------- 7 · Motivation + quick stats ------------------ */

function MotivationAndStats({ motivationLine, quickStats, complianceShort }) {
  const stats = [
    { key: "matches",    label: "Funding partners matched",   value: quickStats.funding_partners_matched ?? 0 },
    { key: "negatives",  label: "Negative items identified",  value: quickStats.negative_items_identified ?? 0 },
    { key: "letters",    label: "Dispute letters generated",  value: quickStats.dispute_letters_generated ?? 0 },
    { key: "funding",    label: "Est. funding potential",     value: fmtDollar(quickStats.estimated_funding_potential) },
    ...(quickStats.estimated_mortgage_purchase_power != null
      ? [{ key: "mortgage", label: "Est. mortgage purchase power", value: fmtDollar(quickStats.estimated_mortgage_purchase_power) }]
      : []),
  ];
  return (
    <section
      data-testid="dashboard-quick-stats"
      className="pt-6 border-t space-y-5"
      style={{ borderColor: "var(--ds-color-border)" }}
    >
      {motivationLine ? (
        <p
          data-testid="dashboard-motivation-line"
          className="text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-3xl"
        >
          {motivationLine}
        </p>
      ) : null}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-5">
        {stats.map((s) => (
          <StatCallout
            key={s.key}
            data-testid={`dashboard-quick-stat-${s.key}`}
            value={s.value}
            label={s.label}
          />
        ))}
      </div>
      {complianceShort ? (
        <p
          data-testid="dashboard-compliance"
          className="text-[12px] text-[var(--ds-color-ink-subtle)] max-w-3xl leading-relaxed"
        >
          {complianceShort}
        </p>
      ) : null}
    </section>
  );
}

/* --------------------------- below the fold --------------------------- */

function BelowTheFold({ summary, user, siteSettings }) {
  const showProfileCta = !user?.funding_profile_complete;
  const showReportCta = !summary.credit_scores || Object.keys(summary.credit_scores).length === 0;

  const cards = summary.achievements?.slice(0, 6);

  return (
    <section data-testid="dashboard-below-fold" className="space-y-10">
      {showProfileCta && (
        <EmptyState
          heading="Start with your funding profile"
          description="A three-minute funding profile unlocks your Readiness Score, partner match, and personalized roadmap."
          primaryCta={{ label: "Complete profile", href: "/profile", icon: Target }}
        />
      )}

      {showReportCta && (
        <div className="grid gap-4" data-testid="dashboard-report-affordances">
          <ConnectCreditPanel />
        </div>
      )}

      {cards?.length ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-3">
            Milestones you&rsquo;ve earned
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {cards.map((a) => (
              <MilestoneCard
                key={a.key}
                status="completed"
                title={a.label}
                completedDate={a.earned_at}
              />
            ))}
          </div>
        </div>
      ) : null}

      <ReadinessGapCard />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SecondaryNavCard testId="dashboard-nav-credit"    to="/credit-report" title="Credit Readiness"    description="Analysis, disputes, tradelines."    Icon={FileText} />
        <SecondaryNavCard testId="dashboard-nav-readiness" to="/readiness"     title="Financial Analyst"   description="Your 0-100 score breakdown."        Icon={TrendingUp} />
        <SecondaryNavCard testId="dashboard-nav-roadmap"   to="/roadmap"       title="Financial Blueprint" description="Your step-by-step plan."            Icon={Compass} />
        <SecondaryNavCard testId="dashboard-nav-partners"  to="/partners"      title="Funding partners"    description="Live matches from vetted lenders." Icon={Handshake} />
      </div>
    </section>
  );
}

function SecondaryNavCard({ to, title, description, Icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group rounded-[var(--ds-radius-lg)] p-5 flex flex-col gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--ds-radius-md)]"
        style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div
        className="mt-2 text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem", lineHeight: 1.35 }}
      >
        {title}
      </div>
      <div className="text-[13px] text-[var(--ds-color-ink-muted)] leading-relaxed">
        {description}
      </div>
      <div
        className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
      >
        Open <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </div>
    </Link>
  );
}

/* ------------------------------- misc --------------------------------- */

function fmtDollar(n) {
  if (n == null) return "—";
  if (typeof n === "string") return n;
  try {
    return "$" + Number(n).toLocaleString("en-US");
  } catch (_) {
    return String(n);
  }
}

function SkeletonHero() {
  return (
    <div
      data-testid="dashboard-loading"
      className="rounded-[var(--ds-radius-xl)] p-10 animate-pulse"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="h-8 w-64 rounded-[var(--ds-radius-md)]" style={{ background: "var(--ds-color-surface-muted)" }} />
      <div className="mt-4 h-4 w-96 rounded-[var(--ds-radius-md)]" style={{ background: "var(--ds-color-surface-muted)" }} />
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 rounded-[var(--ds-radius-lg)]"
            style={{ background: "var(--ds-color-surface-muted)" }}
          />
        ))}
      </div>
    </div>
  );
}

function ErrorPanel({ message }) {
  return (
    <div
      data-testid="dashboard-error"
      className="rounded-[var(--ds-radius-lg)] p-6"
      style={{
        background: "var(--ds-color-danger-soft)",
        border: "1px solid var(--ds-color-danger)",
        color: "var(--ds-color-danger)",
      }}
    >
      <div className="font-semibold">Dashboard temporarily unavailable</div>
      <p className="mt-1 text-[13px]">{message}</p>
    </div>
  );
}

/* ------------------------- Opportunities hero -------------------------- */

function OpportunitiesHeroCard({ summary }) {
  const partnerCount = summary?.quick_stats?.funding_partners_matched ?? 0;
  const mortgageScore = summary?.mortgage_score;
  const mortgageTier = summary?.mortgage_tier;
  return (
    <Link
      to="/opportunities"
      data-testid="dashboard-opportunities-card"
      className="block group rounded-[var(--ds-radius-lg)] p-5 md:p-6 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <div className="grid md:grid-cols-[64px_1fr_auto] gap-4 items-center">
        <div
          className="w-12 h-12 rounded-[var(--ds-radius-md)] flex items-center justify-center flex-shrink-0"
          style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
        >
          <Target className="w-6 h-6" aria-hidden="true" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-1 text-[var(--ds-color-accent)]">
            Opportunities
          </div>
          <h3
            className="text-[var(--ds-color-ink)] leading-tight"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.375rem" }}
          >
            See what you can qualify for today.
          </h3>
          <p className="text-[13px] text-[var(--ds-color-ink-muted)] mt-1.5">
            {[
              partnerCount > 0 ? `${partnerCount} business partner${partnerCount === 1 ? "" : "s"}` : null,
              mortgageScore != null && mortgageTier ? `${mortgageTier.label}` : "Mortgage readiness",
              "Personal credit products",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <span
          data-testid="dashboard-opportunities-cta"
          className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold flex-shrink-0"
          style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
        >
          View my opportunities <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
